import { useEffect, useState } from 'react'
import { getHtmlDataType, HTML_TYPE } from '../utils'
import PdfViewer from './PdfViewer'

interface GenericRenderPreviewProps {
  data: string
  editState?: boolean
  editableRef?: React.RefObject<HTMLDivElement>
  getFormattedHtml?: (editState: boolean, previewData: string) => string
  makeEditableWithRedBackground?: (data: string) => string
  removeEditableAndRedBackground?: (data: string) => string
  variant?: 'correspondence' | 'default' | 'minimal'
  fetchPreviewUrl?: (fileName: string) => Promise<string | undefined>
}

export interface RenderPdfViewerType {
  isBase64: boolean
  variant: 'correspondence' | 'default' | 'minimal'
  data: string
  previewUrl: string | null
}

export interface RenderHtmlContentType {
  getFormattedHtml:
    | ((editState: boolean, previewData: string) => string)
    | undefined
  editableRef: React.RefObject<HTMLDivElement> | undefined
  variant: 'default' | 'correspondence' | 'minimal'
  removeEditableAndRedBackground: ((data: string) => string) | undefined
  editState: boolean | undefined
  data: string
  makeEditableWithRedBackground: ((data: string) => string) | undefined
}

const RenderPdfViewer = ({
  isBase64 = false,
  data,
  previewUrl,
  variant,
}: RenderPdfViewerType) => {
  const pdfProps = {
    pdfUrl: isBase64 ? data : previewUrl || '',
    isBase64,
    allowZoom: false,
    showPageNumber: false,
    defaultZoomScale: 1.3,
  }

  if (variant === 'correspondence') {
    return <PdfViewer {...pdfProps} />
  }

  const wrapperClass =
    variant === 'default'
      ? 'flex editer-wrap justify-center'
      : 'flex editer-wrap justify-center ck'

  const containerClass =
    variant === 'default'
      ? 'w-[49.5rem] min-h-[21cm] ck-content document-editor__editable-container bg-accent-0 p-4 border-t-2 border-secondary-450'
      : 'a4-size with-shadow ck-content document-editor__editable-container bg-accent-0 p-4 border-t-2 border-secondary-450'

  return (
    <div className={wrapperClass}>
      <div className={containerClass}>
        <PdfViewer {...pdfProps} />
      </div>
    </div>
  )
}

const RenderHtmlContent = ({
  getFormattedHtml,
  makeEditableWithRedBackground,
  data,
  editState,
  removeEditableAndRedBackground,
  variant,
  editableRef,
}: RenderHtmlContentType) => {
  const html = getFormattedHtml
    ? getFormattedHtml(!!editState, data)
    : editState && makeEditableWithRedBackground
    ? makeEditableWithRedBackground(data)
    : removeEditableAndRedBackground
    ? removeEditableAndRedBackground(data)
    : data

  const commonHtmlClass = 'overflow-x-auto font-primary leading-4 text-xs'

  if (variant === 'correspondence') {
    return (
      <div className="flex justify-center">
        <div className="a4-size with-shadow bg-accent-0 p-4 border-t-2 border-secondary-450">
          <div
            ref={editableRef}
            dangerouslySetInnerHTML={{ __html: html }}
            className={`bg-inherit ${commonHtmlClass} p-1`}
          />
        </div>
      </div>
    )
  }

  const wrapperClass =
    variant === 'default'
      ? 'p-3 border border-border-100 mt-3 rounded-[0.3re] overflow-x-auto'
      : ''

  const innerClass = `bg-accent-0 ${commonHtmlClass}`

  return (
    <div className="flex justify-center">
      <div className="a4-size with-shadow bg-accent-0 p-4 border-t-2 border-secondary-450">
        <div className={wrapperClass}>
          <div
            ref={editableRef}
            dangerouslySetInnerHTML={{ __html: html }}
            className={innerClass}
          />
        </div>
      </div>
    </div>
  )
}
const GenericRenderPreview = ({
  data,
  editState,
  editableRef,
  getFormattedHtml,
  makeEditableWithRedBackground,
  removeEditableAndRedBackground,
  variant = 'default',
  fetchPreviewUrl,
}: GenericRenderPreviewProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const htmlType = getHtmlDataType(data)

  useEffect(() => {
    let isMounted = true

    const fetchUrl = async () => {
      if (!fetchPreviewUrl) return
      const fetchedUrl = await fetchPreviewUrl(data)

      if (isMounted && fetchedUrl) setPreviewUrl(fetchedUrl)
    }

    if (
      (htmlType === HTML_TYPE.URL_PDF || htmlType === HTML_TYPE.URL_DOCX) &&
      fetchPreviewUrl
    ) {
      fetchUrl()
    }

    return () => {
      isMounted = false
    }
  }, [data])

  if (htmlType === HTML_TYPE.HTML)
    return (
      <RenderHtmlContent
        getFormattedHtml={getFormattedHtml}
        makeEditableWithRedBackground={makeEditableWithRedBackground}
        data={data}
        editState={editState}
        removeEditableAndRedBackground={removeEditableAndRedBackground}
        variant={variant}
        editableRef={editableRef}
      />
    )
  if (htmlType === HTML_TYPE.BASE64_PDF)
    return (
      <RenderPdfViewer
        isBase64={true}
        data={data}
        previewUrl={previewUrl}
        variant={variant}
      />
    )
  if (htmlType === HTML_TYPE.UNKNOWN) return null
  return (
    <RenderPdfViewer
      isBase64={false}
      data={data}
      previewUrl={previewUrl}
      variant={variant}
    />
  )
}

export default GenericRenderPreview
